import { ActionsDictionary, ContextProps } from '@dynamic/ui';
import { Component, FC } from 'react';

import { LayoutData } from '#/client';
import buildDynamicUi, {
  BuildDynamicUiParams,
} from '#/dynamic/components/defaultComponents';

export interface PageRendererProps {
  buildDynamicUiParams: BuildDynamicUiParams;
  pageId?: string | null;
  layout: LayoutData | undefined;
  actions: ActionsDictionary;
  context: any;
}

// Directly calling `buildDynamicUi` from the `PageController`component led to a React warning about changed hook order.
// The reason is that during loading, `PageController` early returned with a spinner, but after loading, it continued to
// build the Dynamic UI components, leading to more hooks called on that side.
//
// The solution was to move `buildDynamicUi` to a separate component, so its hooks don't count in the `PageController`,
// but this led to another issue: the `buildDynamicUi` makes the Dynamic UI factories create new styled components,
// which is not allowed in render methods or functional components.
//
// This is why this must be a class component, so we can do it in the constructor.

/**
 * Builds a Dynamic UI Page component and uses it to render a page.
 * @param props Props object.
 * @param props.buildDynamicUiParams The parameters needed for the `buildDynamicUi` function.
 * @param props.pageId The ID of the page to render. It will be used as the key for the Page component.
 * @param props.layout The layout object for the page, from `usePageController`.
 * @param props.actions The actions object for the page, from `useActions`.
 * @param props.context The context object for the page, from `getPageContext`.
 */
export class PageRenderer extends Component<PageRendererProps> {
  // the DynUi-side type def for the Page component is missing the actions prop, but it's still better than `any`
  private readonly Page: FC<ContextProps & { actions: ActionsDictionary }>;

  constructor(props: PageRendererProps) {
    super(props);
    this.Page = buildDynamicUi(props.buildDynamicUiParams).Page;
  }

  render() {
    const { pageId, layout, actions, context } = this.props;
    const { Page } = this;

    return (
      <Page key={pageId} {...layout} actions={actions} context={context} />
    );
  }
}
